import type { ValidationMessage } from '@/api/workflows';

/** The i18n namespace every rule's error key lives under. */
const PREFIX = 'formRenderer.validation.';

/** Anything that can carry authored error wording: a field, or a repeatable column. */
export type MessageOwner = {
  validationMessages?: ValidationMessage[] | null;
  requiredMessageEn?: string | null;
  requiredMessageFr?: string | null;
  regexMessageEn?: string | null;
  regexMessageFr?: string | null;
};

type Translate = (key: string, params?: Record<string, string | number>) => string;

/** Active language first, then the other one — a single-language author leaves no hole. */
function pick(en: string | null | undefined, fr: string | null | undefined, language: 'en' | 'fr') {
  const [active, other] = language === 'fr' ? [fr, en] : [en, fr];
  return (active ?? '').trim() || (other ?? '').trim() || null;
}

function interpolate(text: string, params?: Record<string, string | number>): string {
  if (!params) return text;
  return text.replace(/\{\{\s*(\w+)\s*\}\}/g, (whole, name: string) =>
    name in params ? String(params[name]) : whole,
  );
}

/**
 * The message for one broken rule on `owner`. In order:
 *
 *  1. the owner's `validationMessages` entry for the rule (`errorKey` without the
 *     `formRenderer.validation.` prefix) — BMO words most errors per field ("Enter amount to
 *     continue", "Please enter a valid fund number."), which one generic catalogue cannot;
 *  2. the older single-rule wording: `requiredMessage` for 'required', `regexMessage` for
 *     'regex.format';
 *  3. the generic catalogue copy.
 *
 * Params (`{{count}}`, `{{field}}`, …) fill authored text exactly as they fill the generic copy.
 */
export function resolveValidationMessage(
  owner: MessageOwner | null | undefined,
  errorKey: string,
  params: Record<string, string | number> | undefined,
  language: 'en' | 'fr',
  t: Translate,
): string {
  const key = errorKey.startsWith(PREFIX) ? errorKey.slice(PREFIX.length) : errorKey;
  const entry = owner?.validationMessages?.find((m) => m.key === key);
  const authored =
    (entry && pick(entry.en, entry.fr, language)) ||
    (key === 'required' && owner
      ? pick(owner.requiredMessageEn, owner.requiredMessageFr, language)
      : null) ||
    (key === 'regex.format' && owner
      ? pick(owner.regexMessageEn, owner.regexMessageFr, language)
      : null);
  return authored ? interpolate(authored, params) : t(errorKey, params);
}
